// src/components/admin/TypographyEditor.jsx
import React, { useState, useEffect } from 'react';
import '../../styles/typography.css';

// List of common web-safe font families
const fontFamilies = [
  'Familjen Grotesk, sans-serif',
  'Wix Madefor Text, sans-serif',
  'Arial, sans-serif',
  'Verdana, sans-serif',
  'Helvetica, sans-serif',
  'Tahoma, sans-serif',
  'Trebuchet MS, sans-serif',
  'Times New Roman, serif',
  'Georgia, serif',
  'Garamond, serif',
  'Courier New, monospace',
  'Brush Script MT, cursive',
  'Montserrat, sans-serif',
  'Open Sans, sans-serif',
  'Roboto, sans-serif',
  'Lato, sans-serif',
  'Poppins, sans-serif'
];

function TypographyEditor({ typography, onUpdate, salonId, onSave, saving, savedMessage, onPreview }) {
  const [headingFont, setHeadingFont] = useState(typography?.headingFont || fontFamilies[0]);
  const [bodyFont, setBodyFont] = useState(typography?.bodyFont || fontFamilies[1]);
  const [fontSize, setFontSize] = useState(typography?.fontSize || 'medium');
  const [errors, setErrors] = useState({
    headingFont: '',
    bodyFont: '',
    fontSize: ''
  });

  // Load Google Fonts dynamically
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Familjen+Grotesk:wght@400;500;600;700&family=Wix+Madefor+Text:wght@400;500;600;700&family=Montserrat:wght@400;700&family=Open+Sans:wght@400;600&family=Roboto:wght@400;700&family=Lato:wght@400;700&family=Poppins:wght@400;600&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    return () => {
      const linkElement = document.querySelector('link[href="' + link.href + '"]');
      if (linkElement) {
        document.head.removeChild(linkElement);
      }
    };
  }, []);

  const validateField = (fieldName, value) => {
    if (!value || value.trim() === '') {
      setErrors(prev => ({ ...prev, [fieldName]: `${fieldName.replace(/([A-Z])/g, ' $1').trim()} is mandatory. Please select a value.` }));
      return false;
    } else {
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
      return true;
    }
  };

  const handleHeadingFontChange = (e) => {
    const newHeadingFont = e.target.value;
    setHeadingFont(newHeadingFont);
    validateField('headingFont', newHeadingFont);

    onUpdate({
      ...typography,
      headingFont: newHeadingFont
    });
  };

  const handleBodyFontChange = (e) => {
    const newBodyFont = e.target.value;
    setBodyFont(newBodyFont);
    validateField('bodyFont', newBodyFont);

    onUpdate({
      ...typography,
      bodyFont: newBodyFont
    });
  };

  const handleFontSizeChange = (e) => {
    const newFontSize = e.target.value;
    setFontSize(newFontSize);
    validateField('fontSize', newFontSize);

    onUpdate({
      ...typography,
      fontSize: newFontSize
    });
  };

  return (
    <div className="typography-editor">
      <div className="editor-page-header">
        <h2>Typography</h2>
        <div className="editor-page-actions">
          {savedMessage && <span className={`editor-save-msg${savedMessage.toLowerCase().includes('error') ? ' error' : ''}`}>{savedMessage}</span>}
          <button className="page-action-btn page-action-preview" type="button" onClick={onPreview}>Live Preview</button>
          <button className="page-action-btn page-action-save" type="button" onClick={onSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
      
      <div className="form-group">
        <label>
          Heading Font <span className="required-asterisk">*</span>
        </label>
        <select
          value={headingFont}
          onChange={handleHeadingFontChange}
          className={errors.headingFont ? 'input-error' : ''}
        >
          {fontFamilies.map((font) => (
            <option key={font} value={font} style={{ fontFamily: font }}>
              {font.split(',')[0]}
            </option>
          ))}
        </select>
        {errors.headingFont && <span className="error-message">{errors.headingFont}</span>}
        <div className="font-preview" style={{ fontFamily: headingFont }}>
          <h3>This is a heading preview</h3>
        </div>
      </div>
      
      <div className="form-group">
        <label>
          Body Font <span className="required-asterisk">*</span>
        </label>
        <select
          value={bodyFont}
          onChange={handleBodyFontChange}
          className={errors.bodyFont ? 'input-error' : ''}
        >
          {fontFamilies.map((font) => (
            <option key={font} value={font} style={{ fontFamily: font }}>
              {font.split(',')[0]}
            </option>
          ))}
        </select>
        {errors.bodyFont && <span className="error-message">{errors.bodyFont}</span>}
        <div className="font-preview" style={{ fontFamily: bodyFont }}>
          <p>This is a paragraph text preview. It shows how your body text will appear throughout the application.</p>
        </div>
      </div>
      
      <div className="form-group">
        <label>
          Font Size <span className="required-asterisk">*</span>
        </label>
        <select
          value={fontSize}
          onChange={handleFontSizeChange}
          className={errors.fontSize ? 'input-error' : ''}
        >
          <option value="small">Small</option>
          <option value="medium">Medium</option>
          <option value="large">Large</option>
        </select>
        {errors.fontSize && <span className="error-message">{errors.fontSize}</span>}
        <div className={`font-size-preview font-size-${fontSize}`}>
          <p>This text demonstrates the selected font size.</p>
        </div>
      </div>

      <div className="preview-card">
        <h4>Combined Preview</h4>
        <div style={{ fontFamily: headingFont }}>
          <h3 style={{ 
            fontSize: fontSize === 'small' ? '20px' : fontSize === 'large' ? '28px' : '24px',
            color: 'var(--primary-coral)',
            margin: '0 0 15px 0'
          }}>
            Your Salon Name
          </h3>
        </div>
        <div style={{ 
          fontFamily: bodyFont,
          fontSize: fontSize === 'small' ? '14px' : fontSize === 'large' ? '18px' : '16px',
          lineHeight: '1.6',
          color: 'var(--dark-gray)'
        }}>
          <p style={{ margin: '0 0 12px 0' }}>
            Welcome to our salon! We provide premium beauty services in a relaxing environment.
          </p>
          <p style={{ margin: '0' }}>
            Our experienced team is dedicated to making you look and feel your best.
          </p>
        </div>
      </div>
    </div>
  );
}

export default TypographyEditor;